/* eslint-disable @stripe-internal/embedded/no-restricted-globals */
import {CONNECT_ELEMENTS_OVERLAY_ZINDEX_DEFAULT} from '@stripe-internal/embedded-theming';
import {deserializeError, serializeError} from 'serialize-error';
import type {IAnalytics, IReports, Tags} from '@sail/observability';
import {urlRedirect} from '@stripe-internal/safe-links';
import type {
  CollectBankAccountTokenResult,
  FinancialConnectionsSessionResult,
} from '@stripe/stripe-js';
import {calculateIframePosition} from '@sail/ui';
import type {SentryTeamName} from '@stripe-internal/connect-embedded-lib';
import {copyToClipboard} from '@sail/utils';
import {isAnalyticsDisabled} from '../data-layer-frame/isAnalyticsDisabled';
import {buildAccessoryLayerFrame} from './accessory-layer-frame/buildAccessoryLayerFrame';
import {buildStripeJsLayerFrame} from './stripejs-layer-frame/buildStripeJsLayerFrame';
import type {ConnectJsOptions} from '../connect/ConnectJsOptions';
import type {
  IFromUILayerMessage,
  IToUILayerMessage,
  IUILayerInit,
  ISerializedInternalLoadErrorEvent,
  UILayerInitOptions,
} from './UILayerMessages';
import type {
  IAccessoryLayerInit,
  IFromAccessoryLayerMessage,
} from './accessory-layer-frame/AccessoryLayerMessages';
import type {
  IStripeJsLayerInit,
  IStripeJsCollectFinancialConnectionsAccountsComplete,
  IStripeJsCollectBankAccountTokenComplete,
  IStripeJsCollectBankAccountForSetupComplete,
  IStripeJsPreloadLinkOnboardingComplete,
  IStripeJsOpenLinkOnboardingComplete,
} from './stripejs-layer-frame/StripeJsLayerMessages';
import {getCurrentScriptUrlContext} from '../utils/getCurrentScriptUrlContext';
import type {EmitEvent} from '../connect/ConnectElementEventEmitter';
import type {
  IOnboardingState,
  OnboardingStateValues,
} from '../data-layer-client/OnboardingContext/OnboardingState';
import type {AccountSessionClaim} from '../data-layer-frame/types';
import {setupMessagePortListener} from './utils/messageChannel';
import type {UpdateOptions} from '../connect/ConnectJSInterface/InitAndUpdateOptionsTypes';
import {getDevLogger} from '../utils/getLogger';
import type {ConnectElementImportKeys} from '../connect/ConnectJSInterface/ConnectElementList';
import {
  CONNECT_ELEMENT_IMPORTS_WITH_ENTRY_POINTS,
  isMobileAuthedWebviewEntryPoint,
} from '../connect/ConnectJSInterface/ConnectElementList';
import type {IFrameMessenger} from '../data-layer-client/FrameMessenger';
import type {
  ConnectElementSupplementalFunctions,
  IConnectElementSupplementalFunction,
  SupplementalFunctionKey,
} from '../connect/ConnectElementSupplementalFunctions';
import {PlatformLayerSupplementalFunctionNotImplementedError} from './errors';
import type {IConnectElementSupplementalObject} from '../connect/ConnectElementSupplementalObjects';
import {getStronglyTypedEntries} from '../utils/getStronglyTypedEntries';
import {removeSearchParam} from '../utils/removeSearchParam';
import {
  UI_LAYER_MESSAGE_CHANNEL_TIMEOUT,
  UI_LAYER_RENDER_TIMEOUT,
} from '../connect/utils/embeddedLoadErrors';
import type {LoggedOutStateValues} from '../connect/LoggedOutState';
import {getSafeAreaDimensions, safeAreaExists} from './utils/mobileUtils';
import type {MessagePortDiagnosticContext} from './utils/IframeErrorReporter';

type IframeLayer =
  | 'ui-layer'
  | 'accessory-layer'
  | 'stripejs-layer'
  | 'pdf-preview-layer';

// TODO: Deduplicate this function from here and IFrameErrorReporter.ts and move to shared utils function
const createSentryErrorAndAnalyticsSender = (
  sendSentryError: (error: Error) => void,
  analytics: IAnalytics,
  connectElement: ConnectElementImportKeys,
  sentryProjectOverride: SentryTeamName | undefined,
  uiLayerId?: string,
) => {
  const {sentryTeam} =
    CONNECT_ELEMENT_IMPORTS_WITH_ENTRY_POINTS[connectElement];

  const sendSentryErrorAndAnalytics = (
    error: Error,
    iframeLayer: IframeLayer,
  ) => {
    if (!isAnalyticsDisabled()) {
      sendSentryError(error);

      // This is used to send analytics events if errors are thrown in the iframe JS that would affect loading the iframes
      analytics.track(`submerchant_surfaces_${iframeLayer}_error`, {
        error: error.message,
        connectElement,
        uiLayerId,
        teamName: sentryProjectOverride || sentryTeam,
      });
    }
  };

  return sendSentryErrorAndAnalytics;
};

const devLogger = getDevLogger();

export default class ConnectElementBaseMessageChannel {
  /** The message channel used to communicate between the platform frame (ConnectElementBaseMessageChannel) and the message port on the UILayerWrapper. */
  private uiLayerMessageChannel: MessageChannel = new MessageChannel();

  /** The map of accessory layer frame names to message channels (which are used to communicate between the platform frame and the corresponding accessory layer). */
  private accessoryLayerMessageChannels: Record<string, MessageChannel> = {};

  /** The map of StripeJS layer frame names to message channels (which are used to communicate between the platform frame and the corresponding StripeJS layer). */
  private stripeJsLayerMessageChannels: Record<string, MessageChannel> = {};

  /** Whether this message channel has been permanently torn down. */
  private destroyed = false;

  /** Bounded state used only to diagnose duplicate UI-layer port handoffs. */
  private uiLayerInitAttempted = false;

  /** Whether the UI-layer port2 transfer has completed successfully. */
  private uiLayerPortHandoffCompleted = false;

  /** The persistent StripeJS layer used for Link onboarding during this Connect session. */
  private linkOnboardingStripeJsLayer:
    | {
        frameId: string;
        messageChannelPromise: Promise<MessageChannel | undefined>;
      }
    | undefined;

  /** The window object of the corresponding UI Layer iframe. This is used to post an `init` message to the UI Layer. */
  private uiLayerWindow: Window;

  /** The target origin of the UI Layer iframe for which to send an init postMessage. */
  private scriptOrigin: string = getCurrentScriptUrlContext().origin;

  /** frame ids created by UI Layer that we can destroy */
  private createdFrameIds: Record<string, string[]> = {};

  /** The function which sends an error report to the Sentry instance. */
  private captureIframeException: (
    error: Error,
    iframeLayer: IframeLayer,
  ) => void;

  private captureMessagePortDiagnostic: (
    error: Error,
    context: MessagePortDiagnosticContext,
  ) => void;

  private setLinkOnboardingStripeJsLayerInteractive = (
    interactive: boolean,
  ) => {
    const frameId = this.linkOnboardingStripeJsLayer?.frameId;
    if (!frameId) return;

    const iframe = document.querySelector<HTMLIFrameElement>(
      `iframe[name="${frameId}"]`,
    );
    if (iframe) {
      iframe.style.pointerEvents = interactive ? 'auto' : 'none';
    }
  };

  private handoffStripeJsLayerPort = (
    stripeJsLayer: Window,
    initMessage: IStripeJsLayerInit,
    port: MessagePort,
  ) => {
    try {
      // nosemgrep: ban-postmessage-calls
      stripeJsLayer.postMessage(
        initMessage,
        getCurrentScriptUrlContext().origin,
        [port],
      );
    } catch (error) {
      try {
        if (error instanceof Error) {
          this.captureMessagePortDiagnostic(error, {
            operation: 'stripejs_layer_init_handoff',
            direction: 'to',
            frame_type: 'stripejs_layer',
            destroyed: this.destroyed,
          });
        }
      } catch (_reportingError) {
        // Diagnostics must never replace the browser's original exception.
      }
      throw error;
    }
  };

  private getLinkOnboardingStripeJsLayer = (
    frameId: string,
    connectJsOptions: ConnectJsOptions,
  ): Promise<MessageChannel | undefined> => {
    if (this.destroyed) {
      return Promise.resolve(undefined);
    }

    if (this.linkOnboardingStripeJsLayer) {
      return this.linkOnboardingStripeJsLayer.messageChannelPromise;
    }

    let messageChannel: MessageChannel | undefined;
    const messageChannelPromise = (async () => {
      const stripeJsLayerPromise = buildStripeJsLayerFrame(
        frameId,
        connectJsOptions.getValues().appearance.variables?.overlayZIndex ||
          CONNECT_ELEMENTS_OVERLAY_ZINDEX_DEFAULT,
        this.connectElement,
        connectJsOptions.getValues().metaOptions
          .mobileAuthedWebviewEntryPointObservabilityProject,
      );
      const iframe = document.querySelector<HTMLIFrameElement>(
        `iframe[name="${frameId}"]`,
      );
      if (iframe) {
        iframe.style.pointerEvents = 'none';
      }

      const stripeJsLayer = await stripeJsLayerPromise;

      // destroy() may run while the iframe is loading. Its cleanup pass has
      // already finished by the time this continuation runs, so do not create
      // a new MessageChannel that would be left behind after teardown.
      if (this.destroyed) {
        return undefined;
      }

      messageChannel = new MessageChannel();
      this.stripeJsLayerMessageChannels[frameId] = messageChannel;
      this.setLinkOnboardingStripeJsLayerInteractive(false);

      const stripeJsLayerInitMessage: IStripeJsLayerInit = {
        requestType: 'initstripejslayer',
        direction: 'to',
        type: 'stripejs-layer-message',
      };
      this.handoffStripeJsLayerPort(
        stripeJsLayer,
        stripeJsLayerInitMessage,
        messageChannel.port2,
      );

      // nosemgrep: ban-message-addeventlistener
      messageChannel.port1.onmessage = (event) =>
        setupMessagePortListener<
          | IStripeJsPreloadLinkOnboardingComplete
          | IStripeJsOpenLinkOnboardingComplete
        >({
          event,
          messageDirection: 'from',
          messageType: 'stripejs-layer-message',
          messageHandlers: {
            preloadlinkonboardingcomplete: ({id}) => {
              this.sendMessageToUILayer('preloadlinkonboardingresult', {id});
            },
            openlinkonboardingcomplete: (values) => {
              this.setLinkOnboardingStripeJsLayerInteractive(false);
              this.sendMessageToUILayer('openlinkonboardingresult', values);
            },
          },
        });
      messageChannel.port1.onmessageerror = () => {};

      return messageChannel;
    })();

    this.linkOnboardingStripeJsLayer = {
      frameId,
      messageChannelPromise,
    };
    messageChannelPromise.catch(() => {
      if (messageChannel) {
        messageChannel.port1.close();
        messageChannel.port2.close();
        if (this.stripeJsLayerMessageChannels[frameId] === messageChannel) {
          delete this.stripeJsLayerMessageChannels[frameId];
        }
      }
      document.querySelector(`iframe[name="${frameId}"]`)?.remove();
      if (
        this.linkOnboardingStripeJsLayer?.messageChannelPromise ===
        messageChannelPromise
      ) {
        this.linkOnboardingStripeJsLayer = undefined;
      }
    });

    return messageChannelPromise;
  };

  /**
   * Creates a wrapper over the message channel used to communicate between the platform frame (ConnectElementBaseMessageChannel)
   * and the message port on the UILayerWrapper.
   * @param iframe The iframe of the UI Layer frame.
   * @param emitEvent The function that emits event when receiving an event message from the uiControllerMessagePort.
   * @param onboardingState The onboarding state of the embedded component.
   * @param connectJsOptions The ConnectJS options of the embedded component.
   * @param connectElement The name of the embedded component.
   * @param frameMessenger The FrameMessenger instance of the Connect instance, for communicating with the data_layer iframe.
   */
  constructor(
    iframe: HTMLIFrameElement,
    emitEvent: EmitEvent,
    onboardingState: IOnboardingState,
    connectJsOptions: ConnectJsOptions,
    private connectElement: ConnectElementImportKeys,
    private frameMessenger: IFrameMessenger,
    private supplementalFunctions: ConnectElementSupplementalFunctions,
    isDisconnected: boolean,
    analytics: IAnalytics,
    reports: IReports | null,
  ) {
    const projectOverride = isMobileAuthedWebviewEntryPoint(this.connectElement)
      ? connectJsOptions.getValues().metaOptions
          .mobileAuthedWebviewEntryPointObservabilityProject
      : undefined;
    this.captureIframeException = createSentryErrorAndAnalyticsSender(
      (error: Error) => {
        if (reports) {
          reports.error(error, {
            // same here
            project:
              projectOverride ??
              CONNECT_ELEMENT_IMPORTS_WITH_ENTRY_POINTS[this.connectElement]
                .sentryTeam,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            tags: this.frameMessenger.getAuthMetadata() as Tags,
          });
        }
      },
      analytics,
      this.connectElement,
      projectOverride,
      iframe.name,
    );
    this.captureMessagePortDiagnostic = (error, context) => {
      try {
        if (reports && !isAnalyticsDisabled()) {
          const diagnosticTags = Object.fromEntries(
            Object.entries(context).map(([key, value]) => [key, String(value)]),
          );
          reports.error(error, {
            project:
              projectOverride ??
              CONNECT_ELEMENT_IMPORTS_WITH_ENTRY_POINTS[this.connectElement]
                .sentryTeam,
            tags: {
              ...this.frameMessenger.getAuthMetadata(),
              iframeLayer:
                context.frame_type === 'ui_layer'
                  ? 'ui-layer'
                  : 'stripejs-layer',
              connectElement: this.connectElement,
              ...diagnosticTags,
            },
          });
        }
      } catch (_reportingError) {
        // Diagnostics must never replace the browser's original exception.
      }
    };

    if (!iframe.contentWindow) {
      // There is a possibility that the iframe may be killed off, e.g. in a SPA/client-side navigation
      // when the user navigates from a page that has the embedded component to another page.
      // Hence, the ConnectJS code may still be running - this check is meant to ensure that the
      // 'UI layer frame is no longer in the DOM' error is safe to ignore.
      if (!document.body.contains(iframe)) {
        if (isDisconnected) {
          // We don't report to Sentry since this behavior is expected
          throw new Error('Custom element was disconnected');
        } else {
          const error = new Error('UI layer frame is no longer in the DOM');
          this.captureIframeException(error, 'ui-layer');
          throw error;
        }
      } else {
        const error = new Error('UI layer frame not loaded');
        this.captureIframeException(error, 'ui-layer');
        throw error;
      }
    }
    this.uiLayerWindow = iframe.contentWindow;

    // These timeouts will trigger a load error to be emitted if the UI Layer does not respond within the specified time
    const uiLayerMessageChannelFailureTimeout = setTimeout(() => {
      const error = new Error(
        `UI layer message channel was not initialized within ${UI_LAYER_MESSAGE_CHANNEL_TIMEOUT}ms`,
      );

      const errorType = 'api_error';
      emitEvent('_internal_loaderror', {error, type: errorType});
      emitEvent('loaderror', {
        error: {type: errorType, message: error.message},
      });
    }, UI_LAYER_MESSAGE_CHANNEL_TIMEOUT);

    // This is set after the UI Layer message channel is initialized
    let uiLayerRenderFailureTimeout: NodeJS.Timeout | undefined;

    // nosemgrep: ban-message-addeventlistener
    this.uiLayerMessageChannel.port1.onmessage = async (event: MessageEvent) =>
      setupMessagePortListener<IFromUILayerMessage>({
        event,
        messageDirection: 'from',
        messageType: 'ui-layer-message',
        messageHandlers: {
          // This event calls the refreshClientSecretCallback via the platform frame's FrameMessenger instance
          refreshclientsecretfrom: async ({messageId}) => {
            await this.frameMessenger.refreshClientSecret.refresh();
            this.sendMessageToUILayer('refreshclientsecretto', {messageId});
          },
          callsupplementalfunction: async (props) => {
            const funcToCall =
              this.supplementalFunctions.values[props.functionKey];

            if (!funcToCall) {
              // We send a reply with an error if the ufnction is not defined
              this.sendMessageToUILayer('supplementalfunctioncallfinished', {
                messageId: props.messageId,
                serializableError: serializeError(
                  new PlatformLayerSupplementalFunctionNotImplementedError(
                    `The supplemental function '${props.functionKey}' is not defined`,
                  ),
                ),
              });
              return;
            }

            try {
              // We use ts-expect-error, as the type we use to track the parameters to call the function `any[]`
              // does not match the parameters expected by the union of all possible callbacks
              // @ts-expect-error - A spread argument must either have a tuple type or be passed to a rest parameter.ts(2556)
              const response = await funcToCall(...props.values);
              this.sendMessageToUILayer('supplementalfunctioncallfinished', {
                messageId: props.messageId,
                values: response,
              });
            } catch (error) {
              this.sendMessageToUILayer('supplementalfunctioncallfinished', {
                messageId: props.messageId,
                serializableError: serializeError(error),
              });
            }
          },
          // This event deletes the accessory layer iframe that is appended to the platform's document.body
          deleteaccessorylayer: ({id}) => {
            const messageChannelToDelete =
              this.accessoryLayerMessageChannels[id];
            if (messageChannelToDelete) {
              messageChannelToDelete.port1.close();
              messageChannelToDelete.port2.close();
              delete this.accessoryLayerMessageChannels[id];
            }

            const frameToDelete = document.querySelector<HTMLIFrameElement>(
              `iframe[name="${id}"]`,
            );
            if (frameToDelete) {
              if (frameToDelete.style.transition !== 'none') {
                frameToDelete.style.opacity = '0';
                frameToDelete.style.transform = 'scale(0.95)';
                setTimeout(() => frameToDelete.remove(), 150);
              } else {
                frameToDelete.remove();
              }
            }
            this.sendMessageToUILayer('deleteaccessorylayer', {id});
          },

          // This event updates the ConnectJS options on the platform frame when the options change on the UI layer
          updateinitoptionsfrom: (values) => {
            connectJsOptions.updateValues(values);
          },

          // This event is used to create a StripeJS iframe and sets up a corresponding message channel
          collectfinancialconnectionsaccounts: async ({
            id,
            clientSecret,
            locale,
            merchantId,
            publishableKey,
            stripeJsEndpoint,
          }) => {
            if (
              connectJsOptions.getValues().metaOptions.mobileSdk &&
              !connectJsOptions.getValues().metaOptions.mobileSdkDurableFlow
            ) {
              emitEvent('mobilefinancialconnectionsrequested', {
                clientSecret,
                id,
                connectedAccountId: merchantId,
              });
            } else {
              const stripeJsLayer = await buildStripeJsLayerFrame(
                id,
                connectJsOptions.getValues().appearance.variables
                  ?.overlayZIndex || CONNECT_ELEMENTS_OVERLAY_ZINDEX_DEFAULT,
                this.connectElement,
                connectJsOptions.getValues().metaOptions
                  .mobileAuthedWebviewEntryPointObservabilityProject,
              );

              const stripeJsLayerMessageChannel = new MessageChannel();
              this.stripeJsLayerMessageChannels[id] =
                stripeJsLayerMessageChannel;

              // Initialize accessory layer message channel
              const stripeJsLayerInitMessage: IStripeJsLayerInit = {
                requestType: 'initstripejslayer',
                direction: 'to',
                type: 'stripejs-layer-message',
              };
              this.handoffStripeJsLayerPort(
                stripeJsLayer,
                stripeJsLayerInitMessage,
                stripeJsLayerMessageChannel.port2,
              );

              stripeJsLayerMessageChannel.port1.postMessage({
                type: 'stripejs-layer-message',
                requestType: 'collectfinancialconnectionsaccounts',
                direction: 'to',
                values: {
                  id,
                  clientSecret,
                  locale,
                  merchantId,
                  publishableKey,
                  stripeJsEndpoint,
                },
              });

              stripeJsLayerMessageChannel.port1.onmessage = (event) =>
                setupMessagePortListener<IStripeJsCollectFinancialConnectionsAccountsComplete>(
                  {
                    event,
                    messageDirection: 'from',
                    messageType: 'stripejs-layer-message',
                    messageHandlers: {
                      collectfinancialconnectionsaccountscomplete: (values) => {
                        this.sendMessageToUILayer(
                          'collectfinancialconnectionsaccountsresult',
                          values,
                        );
                      },
                    },
                  },
                );
              stripeJsLayerMessageChannel.port1.onmessageerror = () => {};
            }
          },

          collectbankaccountforsetup: async (values) => {
            if (this.destroyed) return;
            const {id} = values;
            // Track the frame before awaiting load so destroy() removes it too.
            this.createdFrameIds[iframe.name] =
              this.createdFrameIds[iframe.name] || [];
            this.createdFrameIds[iframe.name].push(id);
            const failed = () =>
              this.sendMessageToUILayer('collectbankaccountforsetupresult', {
                id,
                result: {
                  error: {
                    type: 'api_connection_error',
                    message:
                      'Unable to collect bank account. Please try again.',
                  },
                },
              });
            try {
              const layer = await buildStripeJsLayerFrame(
                id,
                connectJsOptions.getValues().appearance.variables
                  ?.overlayZIndex || CONNECT_ELEMENTS_OVERLAY_ZINDEX_DEFAULT,
                this.connectElement,
                connectJsOptions.getValues().metaOptions
                  .mobileAuthedWebviewEntryPointObservabilityProject,
              );
              if (this.destroyed) return;
              const channel = new MessageChannel();
              this.stripeJsLayerMessageChannels[id] = channel;
              channel.port1.onmessage = (event) =>
                setupMessagePortListener<IStripeJsCollectBankAccountForSetupComplete>(
                  {
                    event,
                    messageDirection: 'from',
                    messageType: 'stripejs-layer-message',
                    messageHandlers: {
                      collectbankaccountforsetupcomplete: (result) =>
                        this.sendMessageToUILayer(
                          'collectbankaccountforsetupresult',
                          result,
                        ),
                    },
                  },
                );
              channel.port1.onmessageerror = failed;
              this.handoffStripeJsLayerPort(
                layer,
                {
                  requestType: 'initstripejslayer',
                  direction: 'to',
                  type: 'stripejs-layer-message',
                },
                channel.port2,
              );
              channel.port1.postMessage({
                type: 'stripejs-layer-message',
                requestType: 'collectbankaccountforsetup',
                direction: 'to',
                values,
              });
            } catch {
              failed();
            }
          },

          collectbankaccounttoken: async ({
            id,
            clientSecret,
            locale,
            merchantId,
            publishableKey,
            stripeJsEndpoint,
          }) => {
            if (
              connectJsOptions.getValues().metaOptions.mobileSdk &&
              !connectJsOptions.getValues().metaOptions.mobileSdkDurableFlow
            ) {
              emitEvent('mobilefinancialconnectionsrequested', {
                clientSecret,
                id,
                connectedAccountId: merchantId,
              });
            } else {
              const stripeJsLayer = await buildStripeJsLayerFrame(
                id,
                connectJsOptions.getValues().appearance.variables
                  ?.overlayZIndex || CONNECT_ELEMENTS_OVERLAY_ZINDEX_DEFAULT,
                this.connectElement,
                connectJsOptions.getValues().metaOptions
                  .mobileAuthedWebviewEntryPointObservabilityProject,
              );
              const stripeJsLayerMessageChannel = new MessageChannel();
              this.stripeJsLayerMessageChannels[id] =
                stripeJsLayerMessageChannel;

              // Initialize accessory layer message channel
              const stripeJsLayerInitMessage: IStripeJsLayerInit = {
                requestType: 'initstripejslayer',
                direction: 'to',
                type: 'stripejs-layer-message',
              };
              this.handoffStripeJsLayerPort(
                stripeJsLayer,
                stripeJsLayerInitMessage,
                stripeJsLayerMessageChannel.port2,
              );

              stripeJsLayerMessageChannel.port1.postMessage({
                type: 'stripejs-layer-message',
                requestType: 'collectbankaccounttoken',
                direction: 'to',
                values: {
                  id,
                  clientSecret,
                  locale,
                  merchantId,
                  publishableKey,
                  stripeJsEndpoint,
                },
              });

              stripeJsLayerMessageChannel.port1.onmessage = (event) =>
                setupMessagePortListener<IStripeJsCollectBankAccountTokenComplete>(
                  {
                    event,
                    messageDirection: 'from',
                    messageType: 'stripejs-layer-message',
                    messageHandlers: {
                      collectbankaccounttokencomplete: (values) => {
                        this.sendMessageToUILayer(
                          'collectbankaccounttokenresult',
                          values,
                        );
                      },
                    },
                  },
                );
              stripeJsLayerMessageChannel.port1.onmessageerror = () => {};
            }
          },

          preloadlinkonboarding: async (values) => {
            if (connectJsOptions.getValues().metaOptions.mobileSdk) {
              this.captureIframeException(
                new Error(
                  'preloadlinkonboarding: reached mobile SDK branch, which should not happen',
                ),
                'stripejs-layer',
              );
            } else {
              const messageChannel = await this.getLinkOnboardingStripeJsLayer(
                values.id,
                connectJsOptions,
              );
              if (!messageChannel) return;
              // nosemgrep: ban-postmessage-calls
              messageChannel.port1.postMessage({
                type: 'stripejs-layer-message',
                requestType: 'preloadlinkonboarding',
                direction: 'to',
                values,
              });
            }
          },

          openlinkonboarding: async (values) => {
            if (connectJsOptions.getValues().metaOptions.mobileSdk) {
              // CARM-3197: We should never reach this branch because we're
              // deciding to hide the Link onboarding UX if using the mobile SDK
              this.captureIframeException(
                new Error(
                  'openlinkonboarding: reached mobile SDK branch, which should not happen',
                ),
                'stripejs-layer',
              );
            } else {
              const messageChannel = await this.getLinkOnboardingStripeJsLayer(
                values.id,
                connectJsOptions,
              );
              if (!messageChannel) return;
              this.setLinkOnboardingStripeJsLayerInteractive(true);
              // nosemgrep: ban-postmessage-calls
              messageChannel.port1.postMessage({
                type: 'stripejs-layer-message',
                requestType: 'openlinkonboarding',
                direction: 'to',
                values,
              });
            }
          },

          // This event is used to create an accessory layer iframe and sets up a corresponding message channel
          requestaccessorylayer: async ({
            id,
            type,
            sourceId,
            initialReposition,
          }) => {
            this.createdFrameIds[sourceId] =
              this.createdFrameIds[sourceId] || [];
            this.createdFrameIds[sourceId].push(id);

            const accessoryLayer = await buildAccessoryLayerFrame(
              id,
              type,
              connectJsOptions.getValues().appearance.variables
                ?.overlayZIndex || CONNECT_ELEMENTS_OVERLAY_ZINDEX_DEFAULT,
              this.connectElement,
              connectJsOptions.getValues().metaOptions
                .mobileAuthedWebviewEntryPointObservabilityProject,
            );

            if (initialReposition) {
              const {attachmentPoints, placement, sourceId, flip} =
                initialReposition;

              const sourceFrame = sourceId.startsWith('stripe-connect-ui-layer')
                ? iframe
                : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  (document.querySelector(
                    `iframe[name="${sourceId}"]`,
                  ) as HTMLIFrameElement);

              const sourceFrameDimensions = sourceFrame.getBoundingClientRect();
              const isFixedFrame =
                getComputedStyle(sourceFrame).position === 'fixed';
              const safeAreaDimensions = getSafeAreaDimensions();

              const positionStyle = calculateIframePosition({
                placement,
                attachmentPoints,
                isFixedFrame,
                // If the source iframe's position is fixed, it does not scroll, e.g. opening an overlay.
                // Hence, we do not want to use the `window.scroll` as we are calculaing the top and left positions
                // without needing to account for scroll.
                // However, if we have a nested popover and the source popover scrolls, this would be incorrect as we would be
                // using the platform's window object instead of the source frame's window object. Nonetheless, this technically
                // should not occur as the popover and tooltip never scroll.
                sourceFrameDimensions: {
                  offsetLeft: isFixedFrame
                    ? sourceFrame.offsetLeft
                    : sourceFrameDimensions.left + window.scrollX,
                  offsetTop: isFixedFrame
                    ? sourceFrame.offsetTop
                    : sourceFrameDimensions.top + window.scrollY,
                },
                accessoryFrameDimensions: {
                  offsetWidth: 0,
                  offsetHeight: 0,
                  scrollHeight: 0,
                  scrollWidth: 0,
                  scrollLeft: 0,
                  scrollTop: 0,
                },
                safeAreaDimensions: safeAreaExists(safeAreaDimensions)
                  ? safeAreaDimensions
                  : undefined,
                flip,
              });

              // Only adjust the top and left positions if it is the initial reposition
              if (positionStyle.top) {
                accessoryLayer.style.top = positionStyle.top;
              }
              if (positionStyle.left) {
                accessoryLayer.style.left = positionStyle.left;
              }
            }

            const accessoryLayerMessageChannel = new MessageChannel();
            this.accessoryLayerMessageChannels[id] =
              accessoryLayerMessageChannel;

            if (!accessoryLayer.contentWindow) {
              const error = new Error(`Accessory layer ${id} failed to load`);
              this.captureIframeException(error, 'accessory-layer');
              throw error;
            }

            // Initialize accessory layer message channel
            const accessoryLayerInitMessage: IAccessoryLayerInit = {
              requestType: 'initaccessorylayer',
              direction: 'to',
              type: 'accessory-layer-message',
            };
            accessoryLayer.contentWindow.postMessage(
              accessoryLayerInitMessage,
              getCurrentScriptUrlContext().origin,
              [accessoryLayerMessageChannel.port2],
            );

            accessoryLayerMessageChannel.port1.onmessage = (event) =>
              setupMessagePortListener<IFromAccessoryLayerMessage>({
                event,
                messageDirection: 'from',
                messageType: 'accessory-layer-message',
                messageHandlers: {
                  resizeiframe: ({styles}) => {
                    // Apply all of the requested styles
                    Object.entries(styles ?? {}).forEach(([key, value]) => {
                      // @ts-expect-error - we don't know all the possible combinations of key and value
                      accessoryLayer.style[key] = value;
                    });
                  },
                },
              });
            accessoryLayerMessageChannel.port1.onmessageerror = () => {};

            this.sendMessageToUILayer('createdaccessorylayer', {id});
          },

          // This event is used to resize and reposition the ui layer or accessory iframes according to the HTML element positioning in the platform frame.
          readjustiframe: ({id, iframeStyles, resize, reposition}) => {
            const isUILayer = id.startsWith('stripe-connect-ui-layer');
            const readjustedFrame = isUILayer
              ? iframe
              : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                (document.querySelector(
                  `iframe[name="${id}"]`,
                ) as HTMLIFrameElement);

            if (!(readjustedFrame instanceof HTMLIFrameElement)) {
              devLogger.error('Failed to find frame (readjustiframe): ', id);
              return;
            }

            // Apply iframe styles
            Object.entries(iframeStyles ?? {}).forEach(([key, value]) => {
              // @ts-expect-error - we don't know all the possible combinations of key and value
              readjustedFrame.style[key] = value;
            });

            // Resize iframe
            if (resize) {
              Object.entries(resize.styles ?? {}).forEach(([key, value]) => {
                // @ts-expect-error - we don't know all the possible combinations of key and value
                readjustedFrame.style[key] = value;
              });
            }

            // Reposition iframe
            if (reposition) {
              const {attachmentPoints, placement, sourceId, flip} = reposition;

              const sourceFrame = sourceId.startsWith('stripe-connect-ui-layer')
                ? iframe
                : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  (document.querySelector(
                    `iframe[name="${sourceId}"]`,
                  ) as HTMLIFrameElement);

              const sourceFrameDimensions = sourceFrame.getBoundingClientRect();
              const isFixedFrame =
                getComputedStyle(sourceFrame).position === 'fixed';
              const safeAreaDimensions = getSafeAreaDimensions();

              const positionStyle = calculateIframePosition({
                placement,
                attachmentPoints,
                isFixedFrame,
                // If the source iframe's position is fixed, it does not scroll, e.g. opening an overlay.
                // Hence, we do not want to use the `window.scroll` as we are calculaing the top and left positions
                // without needing to account for scroll.
                // However, if we have a nested popover and the source popover scrolls, this would be incorrect as we would be
                // using the platform's window object instead of the source frame's window object. Nonetheless, this technically
                // should not occur as the popover and tooltip never scroll.
                sourceFrameDimensions: {
                  offsetLeft: isFixedFrame
                    ? sourceFrame.offsetLeft
                    : sourceFrameDimensions.left + window.scrollX,
                  offsetTop: isFixedFrame
                    ? sourceFrame.offsetTop
                    : sourceFrameDimensions.top + window.scrollY,
                },
                accessoryFrameDimensions: {
                  offsetWidth: readjustedFrame.offsetWidth,
                  offsetHeight: readjustedFrame.offsetHeight,
                  scrollHeight: readjustedFrame.scrollHeight,
                  scrollWidth: readjustedFrame.scrollWidth,
                  scrollLeft: readjustedFrame.scrollLeft,
                  scrollTop: readjustedFrame.scrollTop,
                },
                flip,
                safeAreaDimensions: safeAreaExists(safeAreaDimensions)
                  ? safeAreaDimensions
                  : undefined,
              });

              Object.entries(positionStyle).forEach(([key, value]) => {
                // @ts-expect-error - we don't know all the possible combinations of key and value
                readjustedFrame.style[key] = value;
              });
            }

            // Animate in accessory layer iframes (popovers/tooltips) on their first readjust
            if (!isUILayer && readjustedFrame.style.opacity === '0') {
              readjustedFrame.style.opacity = '1';
              readjustedFrame.style.transform = 'none';
            }

            // Even though the containing iframe has negative margin, the component's size needs to be
            // set explicitly to avoid taking up extra space at the bottom or right in some cases.
            // We skip resizing the parent container for notification banner
            // so that its bordered container always exactly matches the iframe size.
            if (
              isUILayer &&
              this.connectElement !== 'stripe-connect-notification-banner'
            ) {
              const componentDiv = iframe.parentElement;
              if (componentDiv) {
                componentDiv.style.height = `calc(${iframe.style.height} - 8px)`;
                componentDiv.style.width = `calc(${iframe.style.width} - 8px)`;
              }
            }
          },

          // This event is used to emit a HTML event when the HTML attribute on the custom element changes
          emithtmlevent: ({eventType, detail}) => {
            if (eventType === '_internal_loaderror') {
              const internalLoadError =
                detail as ISerializedInternalLoadErrorEvent['detail'];
              emitEvent(eventType, {
                ...internalLoadError,
                error: deserializeError(internalLoadError.error),
              });
              return;
            }

            emitEvent(eventType, detail);
          },

          // This event updates the onboarding state on the platform frame when the UI layer's onboarding state changes
          updateonboardingstate: (values) => {
            if (
              onboardingState.values.isOnboardingComplete ===
              values.isOnboardingComplete
            ) {
              // This is a no-op
              return;
            }

            onboardingState.updateValues(values);
          },

          // This event deletes the StripeJS iframe that is appended to the platform's document.body
          deletestripejslayer: ({id}) => {
            if (this.linkOnboardingStripeJsLayer?.frameId === id) {
              this.linkOnboardingStripeJsLayer = undefined;
            }

            const stripeJsMessageChannelToDelete =
              this.stripeJsLayerMessageChannels[id];
            if (stripeJsMessageChannelToDelete) {
              stripeJsMessageChannelToDelete.port1.close();
              stripeJsMessageChannelToDelete.port2.close();
              delete this.stripeJsLayerMessageChannels[id];
            }

            const stripeJsFrameToDelete = document.querySelector(
              `iframe[name="${id}"]`,
            );
            if (stripeJsFrameToDelete) {
              stripeJsFrameToDelete.remove();
            }
          },

          // This event copies the text from the UI layer to the platform frame's clipboard
          copyText: async (val: string) => {
            await copyToClipboard(val);
          },

          // This event reloads the platform frame's page when the UI layer message sends a message to it
          reloadWindow: () => {
            window.location.reload();
          },

          // This event remove the search param from the platform frame's url when the UI layer message sends a message to with the param name
          removeSearchParam: (param: string) => {
            removeSearchParam(param);
          },

          // This event redirects the platform frame's window when the UI layer message sends a message to it
          redirectWindow: (url: string) => {
            urlRedirect(url);
          },

          reportsuccess: (successType) => {
            switch (successType) {
              case 'messagePort':
                clearTimeout(uiLayerMessageChannelFailureTimeout);

                // Setup the timeout for the UI Layer to render after the message channel is initialized
                uiLayerRenderFailureTimeout = setTimeout(() => {
                  const error = new Error(
                    `UI layer did not render within ${UI_LAYER_RENDER_TIMEOUT}ms`,
                  );

                  const errorType = 'render_error';
                  emitEvent('_internal_loaderror', {error, type: errorType});
                  emitEvent('loaderror', {
                    error: {type: errorType, message: error.message},
                  });
                }, UI_LAYER_RENDER_TIMEOUT);

                break;
              case 'render':
                clearTimeout(uiLayerRenderFailureTimeout);
                break;
              default:
                devLogger.error(
                  `Unknown success type received from UI Layer: ${successType}`,
                );
            }
          },
        },
      });

    this.uiLayerMessageChannel.port1.onmessageerror = () => {};
  }

  private sendMessageToUILayer<T extends IToUILayerMessage>(
    requestType: T['requestType'],
    values?: T extends {values: unknown} ? T['values'] : undefined,
  ) {
    this.uiLayerMessageChannel.port1.postMessage({
      type: 'ui-layer-message',
      direction: 'to',
      requestType,
      values,
    });
  }

  initUILayer(values: UILayerInitOptions) {
    const repeatInit = this.uiLayerInitAttempted;
    const priorHandoffCompleted = this.uiLayerPortHandoffCompleted;
    this.uiLayerInitAttempted = true;

    const initMessage: IUILayerInit = {
      type: 'ui-layer-message',
      requestType: 'init',
      direction: 'to',
      values,
    };

    try {
      this.uiLayerWindow.postMessage(initMessage, this.scriptOrigin, [
        this.uiLayerMessageChannel.port2,
      ]);
      this.uiLayerPortHandoffCompleted = true;
    } catch (error) {
      try {
        if (error instanceof Error) {
          this.captureMessagePortDiagnostic(error, {
            operation: 'ui_layer_init_handoff',
            direction: 'to',
            frame_type: 'ui_layer',
            init_attempt: repeatInit ? 'repeat' : 'first',
            repeat_init: repeatInit,
            prior_handoff_completed: priorHandoffCompleted,
            destroyed: this.destroyed,
          });
        }
      } catch (_reportingError) {
        // Diagnostics must never replace the browser's original exception.
      }
      throw error;
    }
  }

  destroy(uiLayerId: string) {
    this.destroyed = true;

    this.createdFrameIds[uiLayerId]?.forEach((id) => {
      const iframe = document.querySelector(`iframe[name="${id}"]`);
      if (iframe) {
        iframe.remove();
      }
    });

    const linkOnboardingFrameId = this.linkOnboardingStripeJsLayer?.frameId;
    if (linkOnboardingFrameId) {
      document
        .querySelector(`iframe[name="${linkOnboardingFrameId}"]`)
        ?.remove();
    }

    Object.entries(this.stripeJsLayerMessageChannels).forEach(
      ([id, messageChannel]) => {
        messageChannel.port1.close();
        messageChannel.port2.close();
        document.querySelector(`iframe[name="${id}"]`)?.remove();
      },
    );
    this.stripeJsLayerMessageChannels = {};
    this.linkOnboardingStripeJsLayer = undefined;
  }

  updateAccountSession(values: AccountSessionClaim | 'error') {
    this.sendMessageToUILayer('claimresultupdate', values);
  }

  notifyOnboardingStateChanged(values: OnboardingStateValues) {
    this.sendMessageToUILayer('notifyonboardingstatechanged', values);
  }

  notifyLoggedOutStateChanged(values: LoggedOutStateValues) {
    this.sendMessageToUILayer('notifyloggedoutstatechanged', values);
  }

  notifyConnectJsOptionsChanged(values: UpdateOptions) {
    this.sendMessageToUILayer('updateinitoptionsto', values);
  }

  notifyPlatformViewportResize(values: {width: number; height: number}) {
    this.sendMessageToUILayer('platformviewportresize', values);
  }

  updateHTMLAttribute(values: Record<string, string>) {
    this.sendMessageToUILayer('updatehtmlattribute', values);
  }

  updateConnectElementSupplementalObjects(
    values: IConnectElementSupplementalObject,
  ) {
    this.sendMessageToUILayer('updatesupplementalobjects', values);
  }

  interactOutside() {
    this.sendMessageToUILayer('interactoutside');
  }

  scrollOutside() {
    this.sendMessageToUILayer('scrolloutside');
  }

  updateRenderedState(isRendered: boolean) {
    this.sendMessageToUILayer('updaterenderedstateto', {isRendered});
  }

  dataLayerLoaded() {
    this.sendMessageToUILayer('datalayerloaded');
  }

  updateSupplementalFunctionValues(
    values: IConnectElementSupplementalFunction,
  ) {
    const record: Partial<Record<SupplementalFunctionKey, boolean>> = {};

    getStronglyTypedEntries(values).forEach(([key, value]) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      record[key] = !!value;
    });

    this.sendMessageToUILayer('updatesupplementalfunctions', record);
  }

  returnedFromAuthenticatedWebView(url?: string) {
    this.sendMessageToUILayer('onreturnedfromauthenticatedwebview', {url});
  }

  collectMobileFinancialConnectionsResult(values: {
    id: string;
    result: FinancialConnectionsSessionResult | CollectBankAccountTokenResult;
  }) {
    this.sendMessageToUILayer(
      'collectmobilefinancialconnectionsresult',
      values,
    );
  }
}
